/**
 * IDDLGenericPrimitive — contract-driven event emitter for Engine
 * Preview when a concept's `simulation_type` has no shipped primitive.
 *
 * Why this exists:
 *   An admin marks `use_new_engine = true` on a concept (say AI-EV-02,
 *   simulation_type `iddl_ai_chat`) but the rich AI-chat primitive
 *   hasn't been built yet. Without this fallback the admin can't
 *   validate the scoring contract at all — they're stuck on a red
 *   warning screen.
 *
 * What this does:
 *   • Renders the candidate task + goal so the admin sees what the
 *     candidate would read.
 *   • Renders every authored `available_actions` event name as a
 *     clickable button that emits that event (with an optional JSON
 *     event_value).
 *   • Provides a dropdown of `valid_final_decisions` + a button to
 *     emit `final_decision` with the chosen value.
 *   • Lets the admin paste an arbitrary event_name + JSON value for
 *     ad-hoc cases.
 *   • Every emit flows through the same `/api/attempts/{id}/events`
 *     endpoint the real primitives use, so scoring, behaviour logging,
 *     and the Remediation panel all work identically.
 *
 * This is a VALIDATION tool only — it deliberately looks unpolished
 * so no admin mistakes it for the candidate-facing UI.
 */
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Beaker, Send, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function IDDLGenericPrimitive({
  attemptId,
  conceptId,
  scenarioText,
  runtimeConfig,
  onEventLogged,
  onFinalize,
}) {
  const config = runtimeConfig || {};
  const actions = useMemo(() => {
    const set = new Set(config.available_actions || []);
    // Always offer final_decision even if not authored — score depends on it.
    set.add("final_decision");
    return Array.from(set);
  }, [config.available_actions]);

  const [customName, setCustomName] = useState("");
  const [customValue, setCustomValue] = useState("");
  const [pendingFinal, setPendingFinal] = useState(
    (config.valid_final_decisions || [])[0] || "",
  );
  const [busy, setBusy] = useState(false);

  const fire = async (eventName, eventValue) => {
    if (!attemptId) return;
    setBusy(true);
    try {
      const r = await api
        .post(`/attempts/${attemptId}/events`, {
          concept_id: conceptId,
          event_name: eventName,
          event_value: eventValue ?? null,
          screen_context: "generic_primitive",
          client_ts_ms: Date.now(),
        })
        .then((x) => x.data);
      onEventLogged?.(r.event || r);
      toast.success(`fired ${eventName}`, { duration: 1200 });
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || `Failed to fire ${eventName}`,
      );
    } finally {
      setBusy(false);
    }
  };

  const fireCustom = async () => {
    if (!customName.trim()) return;
    let value = null;
    if (customValue.trim()) {
      try {
        value = JSON.parse(customValue);
      } catch (_e) {
        value = customValue.trim();
      }
    }
    await fire(customName.trim(), value);
  };

  const fireFinal = async () => {
    if (!pendingFinal) return;
    await fire("final_decision", { decision: pendingFinal });
    onFinalize?.();
  };

  return (
    <div className="border border-amber-300 bg-amber-50/40 p-4" data-testid="iddl-generic-primitive">
      <div className="flex items-center gap-2 mb-3">
        <Beaker size={14} className="text-amber-700" />
        <span className="font-display font-semibold text-sm">
          Generic Contract Preview
        </span>
        <span className="label-mono ms-auto text-amber-800">
          simulation_type · <code>{config.simulation_type || "(none)"}</code> · admin validation tool
        </span>
      </div>

      <p className="text-xs text-[var(--iddl-muted)] mb-4 italic">
        No rich primitive is shipped yet for this simulation_type. Use the buttons
        below to emit events directly so you can validate the scoring contract,
        sequence rules, and Remediation panel end-to-end before a primitive is
        built.
      </p>

      {(config.candidate_task || config.candidate_goal || scenarioText) && (
        <div className="bg-white border border-[var(--iddl-rule)] p-3 mb-4 space-y-2" data-testid="iddl-generic-context">
          {config.candidate_task && (
            <div>
              <div className="label-mono">Task · candidate instruction</div>
              <p className="text-sm">{config.candidate_task}</p>
            </div>
          )}
          {config.candidate_goal && (
            <div>
              <div className="label-mono">Goal · what is measured</div>
              <p className="text-sm">{config.candidate_goal}</p>
            </div>
          )}
          {scenarioText && (
            <details>
              <summary className="cursor-pointer label-mono">scenario_text (raw)</summary>
              <pre className="text-xs whitespace-pre-wrap mt-1 max-h-48 overflow-y-auto">
                {typeof scenarioText === "string"
                  ? scenarioText
                  : JSON.stringify(scenarioText, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}

      <div className="space-y-3">
        <div>
          <div className="label-mono mb-2">Authored events</div>
          <div className="flex flex-wrap gap-2">
            {actions.map((a) => (
              <Button
                key={a}
                size="sm"
                variant="outline"
                className="rounded-none text-xs"
                disabled={busy || a === "final_decision"}
                onClick={() => fire(a, null)}
                data-testid={`iddl-generic-action-${a}`}
              >
                <Send size={11} className="mr-2" />
                {a}
              </Button>
            ))}
          </div>
        </div>

        <div className="border-t border-[var(--iddl-rule)] pt-3">
          <div className="label-mono mb-2">Final decision</div>
          <div className="flex flex-wrap gap-2 items-center">
            <select
              value={pendingFinal}
              onChange={(e) => setPendingFinal(e.target.value)}
              className="border border-[var(--iddl-rule)] bg-white px-2 py-1 text-xs rounded-none"
              data-testid="iddl-generic-final-select"
            >
              <option value="">(pick a decision)</option>
              {(config.valid_final_decisions || []).map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            <Button
              size="sm"
              className="rounded-none btn-ink"
              disabled={busy || !pendingFinal}
              onClick={fireFinal}
              data-testid="iddl-generic-fire-final"
            >
              <Send size={11} className="mr-2" /> Emit final_decision
            </Button>
            <span className="label-mono text-[var(--iddl-muted)] text-xs">
              { (config.valid_final_decisions || []).length === 0 && "No valid_final_decisions authored." }
            </span>
          </div>
        </div>

        <div className="border-t border-[var(--iddl-rule)] pt-3">
          <div className="label-mono mb-2">Custom event (ad-hoc)</div>
          <div className="flex flex-wrap gap-2 items-center">
            <Input
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder="event_name"
              className="rounded-none text-xs"
              data-testid="iddl-generic-custom-name"
            />
            <Input
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              placeholder='event_value (string or JSON, e.g. {"message_id":"msg-1"})'
              className="rounded-none text-xs flex-1 min-w-[300px]"
              data-testid="iddl-generic-custom-value"
            />
            <Button
              size="sm"
              variant="outline"
              className="rounded-none"
              disabled={busy || !customName.trim()}
              onClick={fireCustom}
              data-testid="iddl-generic-fire-custom"
            >
              <Plus size={11} className="mr-2" /> Fire
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
